/**
 * ============================================================================
 * COORDINATE SYSTEM CONTRACT
 * ============================================================================
 *
 * This is a FRONT-FACING circular maze, not a horizontal tabletop maze. The
 * board reads as roughly flat to the player, but the ball is a genuine 3D rigid
 * body falling under real gravity inside a real 3D physics world.
 *
 *   +Y  up on screen
 *    ^
 *    |        +Z  toward the camera (out of the board)
 *    |       /
 *    |      /
 *    |     /
 *    +----+-------> +X  right on screen
 *
 *  - The maze board lies in the XY plane.
 *  - Z is depth / thickness. The backing plate's FRONT FACE is at z = 0.
 *  - Gravity is (0, -9.81, 0): "down" on screen is -Y.
 *  - The camera sits on +Z looking toward -Z.
 *  - The entire maze assembly rotates about the Z axis.
 *  - Board angles are measured CCW from +X: 90 deg = top, 270 deg = bottom.
 *
 * The ball is confined to a shallow channel along Z: it rests against the plate
 * at the back and is capped by a transparent front cover. Within that channel
 * it moves freely in X and Y, so the maze is a true 3D problem, not a 2D one.
 *
 * See docs/TECHNICAL_DESIGN.md for the full contract and its rationale.
 * ============================================================================
 */

import { DEG2RAD } from '../utils/math';
import { MAZE_SPEC } from './mazeSpec.generated';

/** World gravity, in m/s^2. */
export const GRAVITY: readonly [number, number, number] = [0, -9.81, 0];

/** Physics runs at a fixed 60 Hz regardless of display refresh rate. */
export const FIXED_TIMESTEP = 1 / 60;

/** Upper bound on physics substeps per rendered frame, to avoid a death spiral. */
export const MAX_SUBSTEPS = 5;

/** Incoming frame deltas are clamped to this, in seconds. */
export const MAX_FRAME_DELTA = 0.1;

/**
 * Board dimensions.
 *
 * EVERY value here is read from `blender/spec/maze.json` (via the generated
 * `mazeSpec.generated.ts`) rather than written down again. That is the whole
 * point of the spec: these numbers used to exist twice - once for the Blender
 * models and once here - and they drifted, which is how the game ended up with
 * a collider for a ring the model never drew.
 *
 * If a value needs to change, change it in the spec and run `npm run spec`.
 * Nothing in this file should ever hard-code a board dimension.
 */
export const MAZE = {
  /** Radius of the outer guard. */
  outerRadius: MAZE_SPEC.board.outerRadius,
  /** Radius of the backing plate. */
  plateRadius: MAZE_SPEC.board.plateRadius,
  /** Backing plate thickness along Z. */
  plateThickness: MAZE_SPEC.board.plateThickness,
  /** Z of the plate's front face - the ball rolls against this. */
  plateFrontZ: MAZE_SPEC.board.plateFrontZ,
  /** Z of wall segment centres (aligned with the resting ball's centre). */
  wallZ: MAZE_SPEC.board.wallZ,
  /**
   * Wall extent along Z.
   *
   * Deeper than it strictly needs to be for collision, because with a
   * front-facing camera a wall's height is what makes it read as a wall rather
   * than as a painted line on the plate.
   */
  wallDepth: MAZE_SPEC.board.wallDepth,
  /** Z of the transparent front cover's CENTRE (it spans coverZ +/- coverThickness/2). */
  coverZ: MAZE_SPEC.board.coverZ,
  /** Front cover thickness along Z. */
  coverThickness: MAZE_SPEC.board.coverThickness,
  /** Radius of the raised hub at the very centre of the board. */
  hubRadius: MAZE_SPEC.board.hubRadius,
  /** Height of the centre hub along Z. */
  hubHeight: MAZE_SPEC.board.hubHeight,
  /**
   * Tube radius of the translucent guard's VISUAL torus.
   *
   * The guard is drawn as a torus of this radius centred on `outerRadius`, so
   * its inner surface sits at `outerRadius - guardTube`. The collision wall
   * lines up with that surface rather than with a nominal thickness, or the
   * ball settles too far out and visibly sinks into the guard.
   */
  outerRingTube: MAZE_SPEC.board.guardTube,
} as const;

export const BALL = {
  radius: MAZE_SPEC.ball.radius,
  /** Centre Z when resting against the plate. */
  z: 0.035,
  density: 1200,
  friction: 0.4,
  restitution: 0.3,
  /**
   * Linear damping - the single most load-bearing number in the game.
   *
   * It is not a "feel" knob, it is what makes the maze mean anything. The ball
   * travels in circular channels, so it is subject to a CENTRIFUGAL outward push
   * of `v^2 / r` that competes with gravity's radial component `g * sin(theta)`,
   * which is at most `g`. At the channel radii here (about 1 m) the two cross at
   *
   *     v = sqrt(g * r) = 3.1 m/s.
   *
   * Above that speed the ball is pinned to the OUTER face of whatever channel it
   * is in, no matter where it is on screen, and the whole "gravity pushes it
   * inward in the upper half" routing that the maze is built from stops being
   * true. Measured, with damping at 0.25: the ball reached 6.3 m/s and was flung
   * clean out of the board at r = 1.49, having crossed the channel from the inner
   * face to outside in about three frames. Ring openings and radial walls were
   * decoration; only the guard's exit window mattered.
   *
   * So the ball is damped to stay under that crossover. At 0.25 the measured exit
   * speeds were 3.4-6.3 m/s, the ball was flung clean off the board, and the
   * landings ran from -0.5 to +1.8 - a spread set by the launch, not by the maze.
   * At this value the ball settles near 1.5 m/s in the steepest part of its
   * descent, comfortably under `sqrt(g * r)` everywhere, so gravity decides which
   * face it rides and the landing point follows the exit ANGLE - which is what
   * the player is actually aiming.
   *
   * It also has a useful side effect: damping is proportional to velocity, so it
   * can slow the ball but can never stop it. The ball always keeps descending.
   */
  linearDamping: 4.5,
  /**
   * Angular damping, standing in for ROLLING RESISTANCE.
   *
   * Kept LOW, and that is deliberate - a high value is a trap. Rapier applies
   * this as a torque opposing the ball's spin, so a large value stops the ball
   * ROTATING while it is still being pushed along, which turns a rolling ball
   * into a sliding one and hands the contact friction a free hand. On the steep
   * upper part of the board the channel's radial gravity component supplies the
   * normal force and the tangential component supplies the drive, so sliding
   * friction can simply hold the ball: it sticks for `tan(theta) > 1/mu`, which
   * with friction 0.4 is everything above 68 deg.
   *
   * Measured at 1.6: the ball stuck at `r = inner face + ball radius`, world
   * angle 74-84 deg, within ten frames of release, at four of six board angles.
   * It never reached the maze at all.
   *
   * A rolling ball has no such problem - static friction does no work, so it
   * always descends. The oscillation this value would otherwise allow is damped
   * by `linearDamping` instead, which is the honest place to put it.
   */
  angularDamping: 0.2,
  /** Predictive contact margin - the main anti-tunnelling measure. */
  contactSkin: MAZE_SPEC.ball.contactSkin,
  softCcdPrediction: 0.02,
} as const;

export const WALL = {
  friction: 0.6,
  restitution: 0.1,
} as const;

/**
 * Ball spawn point.
 *
 * In WORLD space, not board space, and that distinction is the point: the ball
 * is fed in from above at the top of the SCREEN, so it always appears at the
 * same place however the board is rotated. It is derived from the outermost
 * channel's midpoint so it starts clear of both the guard and the outermost
 * ring.
 *
 * The small angular offset is deliberate: it puts the ball marginally off the
 * apex so it rolls off deterministically instead of balancing on an unstable
 * equilibrium.
 */
const spawnAngleRad =
  (MAZE_SPEC.ball.spawn.angleDeg + MAZE_SPEC.ball.spawn.angleOffsetDeg) * DEG2RAD;

export const SPAWN = {
  x: MAZE_SPEC.ball.spawn.radius * Math.cos(spawnAngleRad),
  y: MAZE_SPEC.ball.spawn.radius * Math.sin(spawnAngleRad),
} as const;

export const ROTATION = {
  /** Maximum angular speed of the board, rad/s. */
  maxOmega: 1.8,
  /** Angular acceleration while a direction is held, rad/s^2. */
  accel: 9,
  /** Angular deceleration when no direction is held, rad/s^2. */
  decay: 12,
} as const;

/**
 * Mobile / gyroscope input.
 *
 * The gyroscope reads the device's LEFT/RIGHT TILT (`gamma`, -90..90) measured
 * against a NEUTRAL captured when the player enables it, so the control is
 * relative to how they are holding the device, not absolute to the world.
 * `gyroFullTiltDeg` is the tilt from neutral that maps to full board speed;
 * `invertSign` flips left/right after an on-device check (the perceived sign
 * depends on screen orientation and the device's own reporting).
 */
export const MOBILE = {
  gyroDeadZoneDeg: 3,
  gyroFullTiltDeg: 30,
  invertSign: false,
} as const;

/** Arcade-loop settings (Ready → limited balls → GameOver). */
export const GAME = {
  balls: 3,
} as const;

export const CAMERA = {
  fovDeg: 35,
  near: 0.1,
  far: 50,
  /**
   * World Y the framed rectangle is centred on.
   *
   * Zero, because the machine is built around the board's axis: the cabinet runs
   * from about -1.70 (below the scoring row's floor) to +1.705 (the marquee), so
   * its centre is within a couple of millimetres of the origin.
   */
  centreY: 0,
  /**
   * Half-extents of the rectangle that must stay visible, in metres.
   *
   * These describe THE WHOLE CABINET, not the board. The board is 1.25 m in
   * radius; the machine is 1.6 m half-width and 1.95 m half-height, and the
   * design calls for the player to see all four of its stacked sections at once -
   * the marquee at the top, the board in the middle, the scoring row below it and
   * the base with the control panel under that.
   *
   * Measured from `cabinet.glb` (x -1.600..1.600, y -1.950..1.950) plus a 5%
   * margin. Deriving them from the model rather than choosing them is what stops
   * the camera silently cropping the marquee or the pockets when the cabinet is
   * rebuilt.
   */
  fitHalfWidth: 1.68,
  fitHalfHeight: 2.05,
} as const;

/**
 * The 3-2-1 countdown.
 *
 * This is not decoration: it is the AIMING WINDOW. The ball is held at the top
 * of the machine while the player turns the board, so the descent the ball will
 * take is chosen before it is released. On the physical machine the same three
 * seconds serve the same purpose.
 *
 * The ball is genuinely held - its gravity is switched off and its velocity is
 * zeroed every step - rather than being spawned late, so the player can see
 * where it will start from while they aim.
 */
export const COUNTDOWN = {
  /** Whole seconds of 3-2-1. */
  seconds: 3,
  /**
   * How far above its entry point the ball waits, in metres.
   *
   * ZERO, and that is a measured constraint rather than a preference. Lifting the
   * ball in +Y also pushes it OUT in radius - the entry point is already at
   * r = 1.029, only 66 mm inside the guard's inner face at 1.095 - so a lift of
   * 0.22 m put the ball at r = 1.247, OUTSIDE the guard. Released from there it
   * fell down the outside of the board and past the scoring row entirely; the
   * smoke trace read x = 1.38 at y = -0.91 and never entered the maze at all.
   *
   * The ball therefore waits AT its entry point, in the outermost channel at the
   * top of the board. That is also the honest reading of the machine: the ball is
   * fed in at the top outer edge and is already in the maze when the countdown
   * ends, and what the aiming window buys the player is the board's ROTATION, not
   * the ball's height.
   */
  holdLift: 0,
} as const;

/**
 * Where the ball waits while the countdown runs.
 *
 * Derived rather than written down so moving the entry point cannot leave the
 * ball hanging somewhere else - and, as `holdLift` records, so that a lift large
 * enough to push the ball outside the guard cannot be introduced silently.
 */
export const HOLD = {
  x: SPAWN.x,
  y: SPAWN.y + COUNTDOWN.holdLift,
} as const;

/**
 * Round limits.
 *
 * The timer is a BACKSTOP, not a game mechanic. Under pure gravity with
 * velocity-proportional damping the ball always keeps descending, so a round
 * that has not scored has almost always simply not finished yet. The timer
 * exists so a ball wedged in a corner cannot hang the game forever, and so the
 * player is never stuck looking at a screen that has stopped responding.
 */
export const ROUND = {
  /** Simulated seconds a released ball has to reach the row. */
  timeoutSeconds: 60,
} as const;

/** One pocket in the scoring row, as the display and the tests see it. */
export interface SlotTierConfig {
  /** Points for a ball landing in a tier-`tier` pocket. */
  points: number;
  /** HK Traditional Chinese label, shown on the display and the back panel. */
  label: string;
  /** Colour of the pocket's back panel. Presentation only. */
  color: number;
}

/**
 * Tier colours, outermost last.
 *
 * Indexed by the spec's `tier` field, which counts OUTWARD from the centre, so
 * index 0 is the jackpot. Presentation only: nothing resolves a score from a
 * colour, and `SLOT_TIER_COLORS` being shorter than the tier count is handled by
 * clamping rather than by failing.
 *
 * These mirror `SLOT_TIER_MATERIALS` in `blender/scripts/lib/materials.py` so the
 * procedural fallback row is coloured like the modelled one, and they are the
 * PRE-TONE-MAPPING albedos, deliberately darker than the hues you would pick by
 * eye. See the long note in `materials.py`: at this scene's exposure a
 * conventionally-saturated base drives its dominant channel ~2.5x past the
 * display ceiling and ACES then desaturates the whole pocket toward white.
 * Darkening the albedo is the fix; brightening the colour is what caused it.
 */
export const SLOT_TIER_COLORS: readonly number[] = [
  0x7c3aed, // bonus - violet
  0xbe185d, // high  - pink
  0x0d9488, // mid   - teal
  0x1d4ed8, // low   - blue
];

/**
 * Resolve a pocket's appearance from its tier.
 *
 * Clamped rather than indexed directly: the spec allows more tiers than there
 * are colours here, and a nine-pocket row reusing the outermost colour is much
 * better than a build that throws.
 */
export function slotTierColor(tier: number): number {
  return SLOT_TIER_COLORS[Math.min(Math.max(tier, 0), SLOT_TIER_COLORS.length - 1)];
}

/**
 * Round-end conditions.
 *
 * A round ends when the ball comes to rest inside a pocket of the scoring row.
 * The score is resolved from the ball's X *there* rather than at the instant it
 * crossed the row's mouth, because the ball is what the player is watching: if
 * it clipped a divider on the way in and rolled into the neighbouring pocket,
 * the pocket it ends in is the pocket it should be paid for.
 *
 * There is no longer any radial-zone machinery. Nothing inside the maze scores.
 */
export const SCORING = {
  /**
   * Speed below which the ball counts as at rest, m/s.
   *
   * Comfortably above the jitter floor of a resting contact in Rapier. The
   * threshold is far less delicate than it used to be, because it now only has
   * to recognise a ball that has stopped inside a closed box - there is nothing
   * else for a ball in a pocket to do.
   */
  settleSpeed: 0.045,
  /**
   * Consecutive fixed steps below `settleSpeed` before the ball is settled.
   *
   * 24 steps is 0.4 s of simulation, which is long enough to ride out the small
   * bounce a ball makes when it lands on the pocket floor.
   */
  settleSteps: 24,
  /**
   * Distance below the row's floor at which the ball counts as having missed the
   * machine entirely, in metres.
   *
   * A ball that enters a pocket is caught by the row's colliders and can never
   * get below the floor. A ball that MISSES - one that leaves the board outside
   * the row's footprint and sails past it - keeps falling forever, so this is the
   * condition that ends that round.
   *
   * It is a real case, not a hypothetical: the first smoke run put the ball at
   * x = 1.58, well outside the row's 1.364 half-width, and without this it fell
   * for six seconds and would have fallen until the round timer expired.
   */
  fallThroughMargin: 0.35,
  /** Points awarded when the ball never reaches a pocket. */
  ballLostPoints: 0,
} as const;

export const COLORS = {
  background: 0x050a1a,
  backdropInner: 0x12224a,
  backdropOuter: 0x03060f,

  plate: 0x16203a,
  plateEdge: 0x2b3f66,
  hub: 0xffb020,

  outerRing: 0x35c8ff,
  cover: 0x4fd2ff,
  ball: 0xdfe6ee,

  ringRed: 0xe8252f,
  ringYellow: 0xffd21e,
  ringBlue: 0x2f6bff,
  /** Innermost rail. */
  ringGold: 0xffc247,

  neonCyan: 0x22d3ee,
  neonMagenta: 0xff2fb0,

  scoreSilver: 0xc9d6e8,

  // ---- scoring row -------------------------------------------------------
  /** Pocket dividers and the end walls: brushed metal, as modelled. */
  rowDivider: 0x9aa3ad,
  /** The row's floor, kept dark so a resting ball reads against it. */
  rowFloor: 0x14161f,
} as const;
